import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Card from "components/card";
import {
  questionnaireService,
  Questionnaire,
  QuestionnaireResponses,
  QuestionInputType,
  FormattedQuestionnaireResponses,
  FormattedQuestionResponse,
  QuestionnaireSection,
  QuestionnaireQuestion,
} from "services/questionnaireService";
import { MdArrowBack } from "react-icons/md";

// Import our new components
import QuestionSection from "./components/QuestionSection";
import NavigationControls from "./components/NavigationControls";
import ProgressBar from "./components/ProgressBar";
import {
  LoadingScreen,
  ErrorScreen,
  SuccessScreen,
  NotFoundScreen,
} from "./components/StatusScreens";

// Enhanced questionnaire interface with is_completed flag
interface EnhancedQuestionnaire extends Questionnaire {
  is_completed: boolean;
  detail_id: number;
}

const QuestionnaireResponse: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const questionnaireId = searchParams.get("questionnaire");
  const detailId = searchParams.get("detail");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questionnaire, setQuestionnaire] =
    useState<EnhancedQuestionnaire | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [responses, setResponses] = useState<QuestionnaireResponses>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Helper function to initialize response based on input type
  const getInitialResponseByInputType = (
    inputType: QuestionInputType | undefined
  ): string | string[] | boolean | number => {
    switch (inputType) {
      case "single_selection":
        return "";
      case "multiple_selection":
        return false;
      case "text":
      case "text_area":
        return "";
      case "scale_1_10":
        return 0;
      default:
        return "";
    }
  };

  // Fetch questionnaire data
  useEffect(() => {
    const fetchQuestionnaire = async () => {
      if (!token || !questionnaireId || !detailId) {
        setError("Información incompleta para cargar el cuestionario");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await questionnaireService.getQuestionnairesByToken(
          token
        );

        if (response.success) {
          const questionnaires = response.data
            .questionnaires as EnhancedQuestionnaire[];
          const foundQuestionnaire = questionnaires.find(
            (q) => q.id.toString() === questionnaireId
          );

          if (foundQuestionnaire) {
            // Debug the questionnaire structure
            console.log("Loaded questionnaire:", foundQuestionnaire.name);
            console.log("Questionnaire code:", foundQuestionnaire.code);
            console.log(
              "Questionnaire sections:",
              foundQuestionnaire.structure.sections.length
            );

            // Check if any sections have the single_choice_mixed response type
            const hasSingleChoiceMixed =
              foundQuestionnaire.structure.sections.some(
                (section) => section.response_type === "single_choice_mixed"
              );

            console.log(
              "Has single_choice_mixed sections:",
              hasSingleChoiceMixed
            );

            // Debug any single_choice_mixed sections
            if (hasSingleChoiceMixed) {
              foundQuestionnaire.structure.sections.forEach((section, idx) => {
                if (section.response_type === "single_choice_mixed") {
                  console.log(
                    `Section ${idx} (${section.id}) is single_choice_mixed`
                  );
                  console.log(`Section title: "${section.title}"`);
                  console.log(
                    `Section has ${section.questions.length} questions`
                  );

                  // Check if questions have options
                  const questionsWithoutOptions = section.questions.filter(
                    (q) => !q.options || q.options.length === 0
                  );
                  if (questionsWithoutOptions.length > 0) {
                    console.error(
                      `${questionsWithoutOptions.length} questions are missing options in section ${section.id}`
                    );
                    console.error(
                      "First question without options:",
                      questionsWithoutOptions[0]
                    );
                  }
                }
              });
            }

            setQuestionnaire(foundQuestionnaire);

            // Initialize responses object with empty values for each question
            const initialResponses: QuestionnaireResponses = {};
            foundQuestionnaire.structure.sections.forEach((section) => {
              initialResponses[section.id] = {};

              if (section.response_type === "multiple_selection") {
                // For multiple_selection, we'll use an object with question IDs as keys and boolean values
                section.questions.forEach((question) => {
                  initialResponses[section.id][question.id] = false;
                });
              } else if (section.response_type === "scale_1_10") {
                // For scale_1_10, initialize with 0 (no selection)
                section.questions.forEach((question) => {
                  initialResponses[section.id][question.id] = 0;
                });
              } else if (section.response_type === "text_area") {
                // For text_area, initialize with empty string
                section.questions.forEach((question) => {
                  initialResponses[section.id][question.id] = "";
                });
              } else if (section.response_type === "mixed") {
                // For mixed, initialize each question based on its input_type
                section.questions.forEach((question) => {
                  initialResponses[section.id][question.id] =
                    getInitialResponseByInputType(question.input_type);
                });
              } else if (section.response_type === "single_choice") {
                // For single_choice, initialize with empty string or null
                section.questions.forEach((question) => {
                  // For STAI questionnaire, initialize with null to ensure the validation works correctly
                  initialResponses[section.id][question.id] = null;
                });
              } else if (section.response_type === "single_choice_mixed") {
                // For single_choice_mixed, initialize with empty string or null
                section.questions.forEach((question) => {
                  initialResponses[section.id][question.id] = null;
                });
              } else {
                section.questions.forEach((question) => {
                  initialResponses[section.id][question.id] =
                    section.response_type === "multiple_options" ? [] : "";
                });
              }
            });

            setResponses(initialResponses);
          } else {
            setError("No se encontró el cuestionario solicitado");
          }
        } else {
          setError(response.message);
        }
      } catch (err) {
        console.error("Error fetching questionnaire:", err);
        setError(
          "Error al cargar el cuestionario. Por favor, intente nuevamente más tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();
  }, [token, questionnaireId, detailId]);

  // Response handlers
  const handleResponseChange = (
    sectionId: string,
    questionId: string,
    value:
      | string
      | number
      | Array<{
          caracteristica: string;
          es_por_tdah: boolean;
          soy_asi: boolean;
        }>
  ) => {
    console.log(
      `Setting response for ${sectionId}/${questionId} to ${value} (type: ${typeof value})`
    );

    if (
      questionnaire?.structure.sections.find((s) => s.id === sectionId)
        ?.response_type === "text_list"
    ) {
      // For text_list, we need to maintain the array structure
      setResponses((prev) => {
        const newResponses = {
          ...prev,
          [sectionId]: {
            [questionId]: value,
          },
        };
        console.log("New responses for text_list:", newResponses);
        return newResponses;
      });
    } else {
      // Handle other response types as before
      setResponses((prev) => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          [questionId]: value,
        },
      }));
    }
  };

  const handleTextAreaChange = (
    sectionId: string,
    questionId: string,
    value: string
  ) => {
    setResponses((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [questionId]: value,
      },
    }));
  };

  const handleMultipleResponseChange = (
    sectionId: string,
    questionId: string,
    optionValue: string,
    isChecked: boolean
  ) => {
    setResponses((prev) => {
      const currentValues = prev[sectionId][questionId] as string[];
      let newValues: string[];

      if (isChecked) {
        newValues = [...currentValues, optionValue];
      } else {
        newValues = currentValues.filter((v) => v !== optionValue);
      }

      return {
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          [questionId]: newValues,
        },
      };
    });
  };

  const handleMultipleSelectionChange = (
    sectionId: string,
    questionId: string,
    checked: boolean
  ) => {
    setResponses((prev) => {
      const updatedSection = {
        ...prev[sectionId],
        [questionId]: checked,
      };

      return {
        ...prev,
        [sectionId]: updatedSection,
      };
    });
  };

  const handleScaleChange = (
    sectionId: string,
    questionId: string,
    value: number
  ) => {
    setResponses((prev) => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [questionId]: value,
      },
    }));
  };

  // Navigation handlers
  const handleNextSection = () => {
    if (
      questionnaire &&
      currentSectionIndex < questionnaire.structure.sections.length - 1
    ) {
      setCurrentSectionIndex((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex((prev) => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  // Check if current section is complete
  const isCurrentSectionComplete = (section: QuestionnaireSection): boolean => {
    const sectionResponses = responses[section.id] || {};

    // Add debug logging
    console.log("Validating section:", section.id);
    console.log("Response type:", section.response_type);
    console.log("Current responses:", sectionResponses);

    if (section.response_type === "text_list") {
      const items = (sectionResponses[section.questions[0].id] || []) as Array<{
        caracteristica: string;
        es_por_tdah: boolean;
        soy_asi: boolean;
      }>;
      console.log("Text list items:", items);

      // Check if we have at least min_items
      if (!Array.isArray(items) || items.length < (section.min_items || 5)) {
        console.log("Not enough items");
        return false;
      }

      // Check if each item has a non-empty characteristic and either es_por_tdah or soy_asi selected
      const isValid = items.every((item) => {
        const hasCharacteristic =
          item.caracteristica && item.caracteristica.trim() !== "";
        const hasSelection = item.es_por_tdah || item.soy_asi;
        console.log("Item validation:", {
          item,
          hasCharacteristic,
          hasSelection,
        });
        return hasCharacteristic && hasSelection;
      });

      console.log("Section validation result:", isValid);
      return isValid;
    }

    // For multiple_selection, we don't require any selections to proceed
    if (section.response_type === "multiple_selection") {
      return true;
    }

    // Special handling for STAI questionnaire
    if (questionnaire.code === "STAI") {
      // Check if all questions have been answered
      const answeredCount = Object.values(sectionResponses).filter(
        (response) =>
          response !== null && response !== undefined && response !== ""
      ).length;

      console.log(
        `STAI questionnaire: ${answeredCount} of ${section.questions.length} questions answered`
      );

      // If all questions are answered, return true
      return answeredCount === section.questions.length;
    }

    // Debug: Log the response type and responses
    console.log(`Section response type: ${section.response_type}`);
    console.log("Section responses:", sectionResponses);

    const allQuestionsAnswered = section.questions.every((question) => {
      const response = sectionResponses[question.id];

      // Debug: Log each question and its response
      console.log(
        `Question ${question.id}: ${question.text}, Response:`,
        response
      );

      if (section.response_type === "multiple_options") {
        return (response as string[]).length > 0;
      } else if (section.response_type === "scale_1_10") {
        return (response as number) > 0;
      } else if (section.response_type === "text_area") {
        // For text_area, require at least some text (not just whitespace)
        return typeof response === "string" && response.trim().length > 0;
      } else if (section.response_type === "text_list") {
        // For text_list, check if all items have at least one field filled
        if (Array.isArray(response)) {
          const listItems = response as Array<{
            caracteristica: string;
            es_por_tdah: boolean;
            soy_asi: boolean;
          }>;
          console.log("Text list validation:", {
            totalItems: listItems.length,
            items: listItems.map((item) => ({
              caracteristica: item.caracteristica,
              es_por_tdah: item.es_por_tdah,
              soy_asi: item.soy_asi,
              isValid:
                item.caracteristica.trim().length > 0 &&
                (item.es_por_tdah || item.soy_asi),
            })),
          });
          return listItems.every(
            (item) =>
              item.caracteristica.trim().length > 0 &&
              (item.es_por_tdah || item.soy_asi)
          );
        }
        return false;
      } else if (section.response_type === "mixed") {
        // For mixed, check based on the question's input_type
        switch (question.input_type) {
          case "single_selection":
            return !!response;
          case "multiple_selection":
            return true; // Not required
          case "text":
          case "text_area":
            return typeof response === "string" && response.trim().length > 0;
          case "scale_1_10":
            return (response as number) > 0;
          default:
            return !!response;
        }
      } else if (
        section.response_type === "single_choice" ||
        section.response_type === "single_choice_mixed"
      ) {
        // For single_choice or single_choice_mixed, check if a response has been selected
        const isAnswered =
          response !== undefined && response !== null && response !== "";
        console.log(
          `Question ${
            question.id
          } is answered: ${isAnswered}, Response type: ${typeof response}, Value: ${response}`
        );

        // Special handling for numeric values (including 0)
        if (
          typeof response === "number" ||
          (typeof response === "string" && !isNaN(Number(response)))
        ) {
          // For STAI questionnaire, 0 is a valid response
          return true;
        }

        return isAnswered;
      }

      return !!response;
    });

    console.log(`All questions answered: ${allQuestionsAnswered}`);
    return allQuestionsAnswered;
  };

  // Submit questionnaire
  const handleSubmit = async () => {
    if (!questionnaire || !token || !detailId) return;

    setSubmitting(true);

    try {
      // Format the responses to include question text and answer
      const formattedResponses: FormattedQuestionnaireResponses = {};

      // Loop through each section
      questionnaire.structure.sections.forEach((section) => {
        formattedResponses[section.id] = {};

        // Loop through each question
        section.questions.forEach((question) => {
          const questionId = question.id;
          const questionText = question.text;
          const answer = responses[section.id][questionId];

          // Create the formatted response object with structure:
          // { "question": "Question text", "answer": value }
          // This format ensures each question response includes both the question text and its answer
          formattedResponses[section.id][questionId] = {
            question: questionText,
            answer: answer,
          } as FormattedQuestionResponse;
        });
      });

      console.log("Formatted responses:", formattedResponses);

      // Prepare the new payload format
      const payload = {
        questionnaire_id: questionnaire.id,
        responses: formattedResponses,
      };

      console.log("Submitting responses:", payload);

      // Call the updated API endpoint with token in the URL
      const response = await questionnaireService.submitQuestionnaireResponses(
        token,
        payload
      );

      if (response.success) {
        console.log("Submission successful:", response.message);
        setSubmitted(true);

        // After successful submission, navigate back to the questionnaires list
        setTimeout(() => {
          navigate(`/questionnaires?token=${token}`);
        }, 2000);
      } else {
        setError(response.message || "Error al enviar las respuestas");
      }
    } catch (err) {
      console.error("Error submitting questionnaire:", err);
      setError(
        "Error al enviar las respuestas. Por favor, intente nuevamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to navigate back to questionnaires list
  const navigateToQuestionnairesList = () => {
    navigate(`/questionnaires?token=${token}`);
  };

  // Render loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // Render error state
  if (error) {
    return (
      <ErrorScreen
        errorMessage={error}
        token={token}
        onNavigateBack={navigateToQuestionnairesList}
      />
    );
  }

  // Render success state
  if (submitted) {
    return <SuccessScreen />;
  }

  // Render not found state
  if (!questionnaire) {
    return (
      <NotFoundScreen
        token={token}
        onNavigateBack={navigateToQuestionnairesList}
      />
    );
  }

  // Get current section and navigation state
  const currentSection = questionnaire.structure.sections[currentSectionIndex];
  const isLastSection =
    currentSectionIndex === questionnaire.structure.sections.length - 1;
  const isFirstSection = currentSectionIndex === 0;

  // Render main questionnaire UI
  return (
    <div className="min-h-screen bg-gray-100 py-4 md:py-8">
      <div className="container mx-auto max-w-4xl px-3 md:px-4">
        {/* Header with back button and questionnaire name */}
        <div className="mb-4 flex items-center md:mb-6">
          <button
            onClick={navigateToQuestionnairesList}
            className="mr-3 flex items-center justify-center rounded-lg p-2 text-navy-700 transition-all hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700 md:mr-4"
          >
            <MdArrowBack className="h-5 w-5 md:h-6 md:w-6" />
          </button>
          <h1 className="truncate text-xl font-bold text-navy-700 dark:text-white md:text-2xl">
            {questionnaire.name}
          </h1>

          {/* Debug info for single_choice_mixed */}
          {questionnaire.structure.sections.some(
            (s) => s.response_type === "single_choice_mixed"
          ) && (
            <span className="ml-2 rounded-md bg-yellow-100 px-2 py-1 text-xs text-yellow-800">
              single_choice_mixed
            </span>
          )}
        </div>

        {/* Progress bar */}
        <ProgressBar
          currentSectionIndex={currentSectionIndex}
          totalSections={questionnaire.structure.sections.length}
        />

        <Card extra="w-full p-4 md:p-6 mb-6 md:mb-8">
          {/* Section title and description */}
          <div className="mb-4 md:mb-6">
            <h2 className="mb-2 text-lg font-bold text-navy-700 dark:text-white md:text-xl">
              {currentSection.title}

              {/* Show response type in debug mode */}
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({currentSection.response_type})
              </span>
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 md:text-base">
              {currentSection.description}
            </p>
          </div>

          {/* Questions */}
          <div className="space-y-6 md:space-y-8">
            {/* Error message if section type not supported */}
            {![
              "single_choice",
              "multiple_options",
              "multiple_selection",
              "scale_1_10",
              "text_area",
              "mixed",
              "single_choice_mixed",
              "text_list",
            ].includes(currentSection.response_type) && (
              <div className="rounded-lg bg-red-100 p-4 text-red-700">
                Error: Unsupported response type "{currentSection.response_type}
                "
              </div>
            )}

            <QuestionSection
              section={currentSection}
              responses={responses}
              handleResponseChange={handleResponseChange}
              handleTextAreaChange={handleTextAreaChange}
              handleMultipleResponseChange={handleMultipleResponseChange}
              handleMultipleSelectionChange={handleMultipleSelectionChange}
              handleScaleChange={handleScaleChange}
            />
          </div>

          {/* Navigation controls */}
          <NavigationControls
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
            isCurrentSectionComplete={isCurrentSectionComplete(currentSection)}
            submitting={submitting}
            onPrevious={handlePreviousSection}
            onNext={handleNextSection}
            onSubmit={handleSubmit}
          />
        </Card>
      </div>
    </div>
  );
};

export default QuestionnaireResponse;
